import Phaser from 'phaser';
import type { GameState } from './GameState';
import type { GameServices } from './GameServices';
import { SERVICES_KEY } from './GameServices';

/**
 * Base class for all Biomass gameplay scenes. Provides:
 * - Typed access to runtime services (state, settings, saveManager, arcs)
 * - Automatic shutdown hook subclasses implement for cleanup
 * - A convention for resetting scene-local data on create()
 *
 * Phaser's scene.restart() reuses the same instance without re-running
 * class-field initializers. Subclasses must put their reset logic in
 * resetSceneState() so restart is clean.
 */
export abstract class BiomassScene extends Phaser.Scene {
  protected services!: GameServices;
  protected state!: GameState;

  create(): void {
    const services = this.game.registry.get(SERVICES_KEY) as GameServices | undefined;
    if (!services) {
      throw new Error(
        `BiomassScene requires GameServices on game.registry.${SERVICES_KEY}`,
      );
    }
    this.services = services;
    this.state = services.state;
    this.resetSceneState();
    this.onSceneCreate();
  }

  /**
   * Teardown hook called by Phaser on scene shutdown (restart, stop, etc).
   * Override to release scene-local resources. Base implementation calls
   * resetSceneState() to keep the instance clean.
   */
  shutdown(): void {
    this.onSceneShutdown();
    this.resetSceneState();
  }

  /**
   * Reset scene-local data. Called on create() AND shutdown(). Must be
   * safe to call multiple times. Use this to clear entity arrays, Maps,
   * timers, etc. that are scoped to a single playthrough of this scene.
   */
  protected abstract resetSceneState(): void;

  /** Main scene setup — runs after resetSceneState() on every create. */
  protected abstract onSceneCreate(): void;

  /** Optional hook for extra shutdown cleanup. Default is a no-op. */
  protected onSceneShutdown(): void {
    // default no-op
  }

  static readonly SERVICES_KEY = SERVICES_KEY;
}
